import React from 'react';

const QuotationTemplate = React.forwardRef(({ data, catalog }, ref) => {
  const {
    client_name = '',
    client_greeting = 'Estimado/a',
    items = [],
    alternatives = [],
    total_neto = 0,
    iva_amount = 0,
    total_con_iva = 0,
    validity_start,
    validity_end,
    notes,
    agent_identity_id,
    campaign_id,
    reference_type,
  } = data || {};

  const { agents = [], campaigns = [] } = catalog || {};

  // --- Helpers ---

  const agent = agents.find(a => a.agent_identity_id === agent_identity_id) || {};
  const campaign = campaigns.find(c => c.id === campaign_id) || {};

  const formatPrice = (n) => {
    if (n == null || isNaN(n)) return '$0';
    return '$' + Number(n).toLocaleString('es-CL');
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const d = new Date(dateStr + 'T12:00:00');
    return `${d.getDate()} de ${months[d.getMonth()]} del ${d.getFullYear()}`;
  };

  const getEffectivePrice = (item) => {
    if (item.override_price != null) return item.override_price;
    if (item.discount_price != null) return item.discount_price;
    return item.normal_price;
  };

  const getProductImage = (subcategory) => {
    const map = {
      'Licencia Anual': '/quotation-assets/kit-pos.png',
      'Licencia de por Vida': '/quotation-assets/kit-pos.png',
      'Licencia Mensual': '/quotation-assets/kit-pos.png',
      'PC Touch': '/quotation-assets/pc-touch.png',
      'PC AIO': '/quotation-assets/pc-aio.png',
      'PC Desktop': '/quotation-assets/pc-desktop.jpg',
      'Mini PC': '/quotation-assets/pc-desktop.jpg',
      'Notebook': '/quotation-assets/pc-aio.png',
      'Impresora Termica': '/quotation-assets/impresora.png',
      'Lector Codigo Barras': '/quotation-assets/lector.png',
      'Gaveta de Dinero': '/quotation-assets/gaveta.jpg',
      'Servicio Instalacion': '/quotation-assets/logo-clubalmacen.png',
    };
    return map[subcategory] || '/quotation-assets/kit-pos.png';
  };

  const todayFormatted = formatDateLong(new Date().toISOString().split('T')[0]);

  const referenceText = reference_type
    ? `Cotizacion ${reference_type}`
    : 'Cotizacion Kit POS ClubAlmacen';

  // --- Shared styles ---

  const pageStyle = {
    width: '190mm',
    minHeight: '277mm',
    padding: '0 13mm',
    margin: '0 auto',
    position: 'relative',
    boxSizing: 'border-box',
  };

  const footerStyle = {
    position: 'absolute',
    bottom: '8mm',
    left: '13mm',
    right: '13mm',
    textAlign: 'center',
    fontSize: '9px',
    color: '#888',
    borderTop: '1px solid #ccc',
    paddingTop: '6px',
    lineHeight: '1.5',
  };

  const sectionTitleStyle = {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '10px',
    marginTop: '18px',
  };

  // --- Shared header component (toldo + logo) ---

  const renderHeader = () => (
    <div style={{ marginLeft: '-13mm', marginRight: '-13mm', marginBottom: '12px' }}>
      {/* Toldo image - full width decorative awning */}
      <img
        src="/quotation-assets/toldo.png"
        alt=""
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
        }}
      />
      {/* Logo bar below toldo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 20px 4px 20px',
        }}
      >
        <img
          src="/quotation-assets/logo-clubalmacen.png"
          alt="ClubAlmacen.com"
          style={{ height: '48px', width: 'auto' }}
        />
        <div
          style={{
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#E87722',
            textAlign: 'right',
            letterSpacing: '0.5px',
          }}
        >
          KIT POS CLUBALMACEN
        </div>
      </div>
    </div>
  );

  // --- Shared footer component ---

  const renderFooter = () => (
    <div style={footerStyle}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <img
          src="/quotation-assets/logo-el-almacen.png"
          alt="El Almacen"
          style={{ height: '16px', width: 'auto', verticalAlign: 'middle' }}
        />
      </div>
      <div>www.ClubAlmacen.com | www.ElAlmacen.cl</div>
      <div>Club Almacen E.I.R.L. - San Martin 553 Of. 602 - Valparaiso - Chile</div>
    </div>
  );

  // --- Render ---

  return (
    <div
      ref={ref}
      style={{
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#333',
        fontSize: '12px',
        lineHeight: '1.5',
        width: '216mm',
        margin: '0 auto',
        background: '#fff',
      }}
    >
      {/* ============================================ */}
      {/* PAGE 1: PORTADA                              */}
      {/* ============================================ */}
      <div style={pageStyle}>
        {renderHeader()}

        {/* Campaign banner */}
        {campaign.name && (
          <div
            style={{
              background: '#FFF3E0',
              border: '2px solid #E87722',
              borderRadius: '6px',
              padding: '12px 16px',
              textAlign: 'center',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
            }}
          >
            <img
              src="/quotation-assets/banner-cybermonday.png"
              alt={campaign.name}
              style={{ height: '50px', width: 'auto' }}
            />
            <div
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#E87722',
              }}
            >
              {campaign.name}
            </div>
          </div>
        )}

        {/* Date */}
        <div
          style={{
            textAlign: 'right',
            fontSize: '12px',
            color: '#555',
            marginBottom: '16px',
          }}
        >
          {todayFormatted}
        </div>

        {/* Reference */}
        <div
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '18px',
          }}
        >
          Referencia: {referenceText}
        </div>

        {/* Greeting */}
        <div
          style={{
            fontSize: '13px',
            marginBottom: '14px',
          }}
        >
          {client_greeting} {client_name || '[Nombre del cliente]'}:
        </div>

        {/* Campaign promotional paragraph */}
        <div
          style={{
            fontSize: '12px',
            lineHeight: '1.7',
            marginBottom: '14px',
            textAlign: 'justify',
          }}
        >
          {campaign.promo_text ? (
            <span>{campaign.promo_text}</span>
          ) : (
            <span>
              Le hacemos llegar nuestra cotizacion con los precios especiales de nuestra campana vigente.
              Los precios indicados en esta cotizacion tienen un descuento especial que solo es valido
              durante el periodo de la campana.
            </span>
          )}
          {validity_end && (
            <span>
              {' '}Aprovecha estos precios hasta el{' '}
              <span style={{ fontWeight: 'bold', color: '#D32F2F' }}>
                {formatDateLong(validity_end)}
              </span>
              .
            </span>
          )}
        </div>

        {/* Formal closing paragraph */}
        <div
          style={{
            fontSize: '12px',
            lineHeight: '1.7',
            marginBottom: '20px',
            textAlign: 'justify',
          }}
        >
          Quedamos atentos a sus consultas y le invitamos a revisar el detalle de los productos,
          condiciones y formas de pago incluidos en esta cotizacion.
        </div>

        {/* Software screenshot */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '24px',
          }}
        >
          <img
            src="/quotation-assets/software-screenshot.png"
            alt="Software El Almacen - Punto de Venta"
            style={{
              width: '100%',
              maxWidth: '480px',
              height: 'auto',
              borderRadius: '4px',
              border: '1px solid #E0E0E0',
            }}
          />
        </div>

        {/* Agent details */}
        <div
          style={{
            borderTop: '1px solid #ddd',
            paddingTop: '14px',
            marginBottom: '10px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>
            {agent.name || '[Nombre del asesor]'}
          </div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            {agent.role_title || 'Asesor Comercial'}
          </div>
          <div style={{ fontSize: '11px', color: '#E87722', fontWeight: '600', marginTop: '2px' }}>
            Club Almacen
          </div>
          {agent.phone && (
            <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
              Tel: {agent.phone}
            </div>
          )}
          {agent.email && (
            <div style={{ fontSize: '11px', color: '#555', marginTop: '1px' }}>
              {agent.email}
            </div>
          )}
        </div>

        {renderFooter()}
      </div>

      {/* ============================================ */}
      {/* PAGE 2: PRODUCT TABLE                        */}
      {/* ============================================ */}
      <div style={{ ...pageStyle, pageBreakBefore: 'always' }}>
        {renderHeader()}

        {/* Main product table */}
        {items.length > 0 && (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '2px solid #E87722',
              marginBottom: '16px',
              fontSize: '11px',
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    background: '#E87722',
                    color: '#fff',
                    padding: '8px 6px',
                    textAlign: 'center',
                    width: '40px',
                    fontSize: '11px',
                    fontWeight: '700',
                  }}
                >
                  CANT
                </th>
                <th
                  style={{
                    background: '#E87722',
                    color: '#fff',
                    padding: '8px 10px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: '700',
                  }}
                >
                  Descripcion del producto
                </th>
                <th
                  style={{
                    background: '#E87722',
                    color: '#fff',
                    padding: '8px 10px',
                    textAlign: 'center',
                    width: '140px',
                    fontSize: '10px',
                    fontWeight: '700',
                    lineHeight: '1.3',
                  }}
                >
                  {campaign.name ? 'CAMPANA' : 'PRECIO'}
                  <br />
                  (Valores sin IVA)
                </th>
                <th
                  style={{
                    background: '#E87722',
                    color: '#fff',
                    padding: '8px 6px',
                    textAlign: 'center',
                    width: '70px',
                    fontSize: '10px',
                    fontWeight: '700',
                  }}
                >
                  Imagen
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const effectivePrice = getEffectivePrice(item);
                const hasDiscount =
                  (item.discount_price != null || item.override_price != null) &&
                  effectivePrice < item.normal_price;

                return (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: '1px solid #ddd',
                    }}
                  >
                    {/* Quantity */}
                    <td
                      style={{
                        padding: '10px 6px',
                        textAlign: 'center',
                        verticalAlign: 'top',
                        fontWeight: '600',
                        fontSize: '13px',
                        borderRight: '1px solid #ddd',
                      }}
                    >
                      {item.quantity}
                    </td>

                    {/* Product name + description */}
                    <td
                      style={{
                        padding: '10px',
                        verticalAlign: 'top',
                        borderRight: '1px solid #ddd',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: '700',
                          fontSize: '12px',
                          color: '#222',
                          marginBottom: '4px',
                        }}
                      >
                        {item.name}
                      </div>
                      {item.subcategory && (
                        <div
                          style={{
                            fontSize: '10px',
                            color: '#666',
                            marginBottom: '2px',
                          }}
                        >
                          {item.subcategory}
                        </div>
                      )}
                    </td>

                    {/* Price */}
                    <td
                      style={{
                        padding: '10px',
                        textAlign: 'center',
                        verticalAlign: 'top',
                        borderRight: '1px solid #ddd',
                      }}
                    >
                      {hasDiscount ? (
                        <div>
                          <div
                            style={{
                              textDecoration: 'line-through',
                              color: '#999',
                              fontSize: '11px',
                              marginBottom: '4px',
                            }}
                          >
                            Normal: {formatPrice(item.normal_price)}
                          </div>
                          <div
                            style={{
                              color: '#2E7D32',
                              fontWeight: 'bold',
                              fontSize: '13px',
                            }}
                          >
                            {formatPrice(effectivePrice)}
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            fontWeight: '600',
                            fontSize: '13px',
                          }}
                        >
                          {formatPrice(item.normal_price)}
                        </div>
                      )}
                    </td>

                    {/* Product image */}
                    <td
                      style={{
                        padding: '6px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                      }}
                    >
                      <img
                        src={getProductImage(item.subcategory)}
                        alt={item.subcategory || item.name}
                        style={{
                          width: '56px',
                          height: '56px',
                          objectFit: 'contain',
                          borderRadius: '3px',
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* Totals */}
            <tfoot>
              <tr
                style={{
                  borderTop: '2px solid #E87722',
                }}
              >
                <td
                  colSpan={2}
                  style={{
                    padding: '8px 10px',
                    textAlign: 'right',
                    fontWeight: '700',
                    fontSize: '12px',
                    color: '#333',
                  }}
                >
                  TOTAL NETO CON DESCUENTO:
                </td>
                <td
                  style={{
                    padding: '8px 10px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#2E7D32',
                  }}
                >
                  {formatPrice(total_neto)}
                </td>
                <td></td>
              </tr>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: '4px 10px 8px',
                    textAlign: 'right',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#222',
                  }}
                >
                  TOTAL CON IVA:
                </td>
                <td
                  style={{
                    padding: '4px 10px 8px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: '#E87722',
                  }}
                >
                  {formatPrice(total_con_iva)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}

        {/* Alternatives section */}
        {alternatives.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#555',
                marginBottom: '10px',
              }}
            >
              Otras alternativas:
            </div>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #ccc',
                fontSize: '11px',
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      background: '#666',
                      color: '#fff',
                      padding: '6px',
                      textAlign: 'center',
                      width: '40px',
                      fontSize: '10px',
                    }}
                  >
                    CANT
                  </th>
                  <th
                    style={{
                      background: '#666',
                      color: '#fff',
                      padding: '6px 10px',
                      textAlign: 'left',
                      fontSize: '10px',
                    }}
                  >
                    Producto alternativo
                  </th>
                  <th
                    style={{
                      background: '#666',
                      color: '#fff',
                      padding: '6px 10px',
                      textAlign: 'center',
                      width: '120px',
                      fontSize: '10px',
                    }}
                  >
                    Precio (sin IVA)
                  </th>
                  <th
                    style={{
                      background: '#666',
                      color: '#fff',
                      padding: '6px',
                      textAlign: 'center',
                      width: '60px',
                      fontSize: '10px',
                    }}
                  >
                    Imagen
                  </th>
                </tr>
              </thead>
              <tbody>
                {alternatives.map((item, idx) => {
                  const effectivePrice = getEffectivePrice(item);
                  const hasDiscount =
                    (item.discount_price != null || item.override_price != null) &&
                    effectivePrice < item.normal_price;

                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td
                        style={{
                          padding: '8px 6px',
                          textAlign: 'center',
                          fontWeight: '600',
                          borderRight: '1px solid #eee',
                        }}
                      >
                        {item.quantity}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          borderRight: '1px solid #eee',
                        }}
                      >
                        <div style={{ fontWeight: '600', fontSize: '11px' }}>{item.name}</div>
                        {item.subcategory && (
                          <div style={{ fontSize: '10px', color: '#777' }}>{item.subcategory}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', borderRight: '1px solid #eee' }}>
                        {hasDiscount ? (
                          <div>
                            <div
                              style={{
                                textDecoration: 'line-through',
                                color: '#999',
                                fontSize: '10px',
                              }}
                            >
                              {formatPrice(item.normal_price)}
                            </div>
                            <div
                              style={{
                                color: '#2E7D32',
                                fontWeight: 'bold',
                                fontSize: '12px',
                              }}
                            >
                              {formatPrice(effectivePrice)}
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontWeight: '600' }}>{formatPrice(item.normal_price)}</div>
                        )}
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center', verticalAlign: 'middle' }}>
                        <img
                          src={getProductImage(item.subcategory)}
                          alt={item.subcategory || item.name}
                          style={{
                            width: '44px',
                            height: '44px',
                            objectFit: 'contain',
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {notes && (
          <div
            style={{
              marginTop: '16px',
              padding: '10px 14px',
              background: '#FFFDE7',
              border: '1px solid #FFF9C4',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#555',
            }}
          >
            <strong>Nota:</strong> {notes}
          </div>
        )}

        {renderFooter()}
      </div>

      {/* ============================================ */}
      {/* PAGE 3: CONDITIONS & VALIDITY                */}
      {/* ============================================ */}
      <div style={{ ...pageStyle, pageBreakBefore: 'always' }}>
        {renderHeader()}

        {/* Conditions */}
        <div style={sectionTitleStyle}>Condiciones:</div>
        <div
          style={{
            paddingLeft: '8px',
            fontSize: '12px',
            lineHeight: '2',
            color: '#444',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '6px' }}>
            <img
              src="/quotation-assets/logo-blueexpress.png"
              alt="Blue Express"
              style={{ height: '18px', width: 'auto', marginTop: '4px', flexShrink: 0 }}
            />
            <span>Envio a todo Chile por encomienda Blue Express o al domicilio del cliente dentro de Santiago.</span>
          </div>
          <div style={{ paddingLeft: '28px', marginBottom: '4px' }}>
            Chat MiClub para dudas y soporte post venta.
          </div>
          <div style={{ paddingLeft: '28px', marginBottom: '4px' }}>
            Ayudas de Inicio para aprender a usar el software.
          </div>
          <div style={{ paddingLeft: '28px', marginBottom: '4px' }}>
            Precios no incluyen IVA salvo donde se indique.
          </div>
          <div style={{ paddingLeft: '28px', marginBottom: '4px' }}>
            Todos los productos incluyen garantia del fabricante.
          </div>
        </div>

        {/* Validity */}
        <div style={sectionTitleStyle}>Validez:</div>
        <div
          style={{
            fontSize: '12px',
            lineHeight: '1.8',
            color: '#444',
          }}
        >
          {validity_start && validity_end ? (
            <p>
              Esta cotizacion es valida desde el{' '}
              <span style={{ fontWeight: 'bold', color: '#D32F2F' }}>
                {formatDateLong(validity_start)}
              </span>{' '}
              hasta el{' '}
              <span style={{ fontWeight: 'bold', color: '#D32F2F' }}>
                {formatDateLong(validity_end)}
              </span>
              .
            </p>
          ) : validity_end ? (
            <p>
              Esta cotizacion es valida hasta el{' '}
              <span style={{ fontWeight: 'bold', color: '#D32F2F' }}>
                {formatDateLong(validity_end)}
              </span>
              .
            </p>
          ) : (
            <p>Validez sujeta a confirmacion. Consulte con su asesor.</p>
          )}
          <p style={{ marginTop: '8px' }}>
            Los precios de campana estan sujetos a disponibilidad de stock y vigencia de la promocion.
            Fuera del periodo indicado, aplican los precios normales de lista.
          </p>
        </div>

        {/* Campaign validity text if available */}
        {campaign.validity_text && (
          <div
            style={{
              marginTop: '16px',
              padding: '10px 14px',
              background: '#FFF3E0',
              border: '1px solid #FFE0B2',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#E65100',
              lineHeight: '1.6',
            }}
          >
            {campaign.validity_text}
          </div>
        )}

        {/* Badges section */}
        <div
          style={{
            marginTop: '28px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          <img
            src="/quotation-assets/recomendado-clubalmacen.png"
            alt="Recomendado por Club Almacen"
            style={{ height: '64px', width: 'auto' }}
          />
          <img
            src="/quotation-assets/sii-boleta.png"
            alt="SII Boleta Electronica"
            style={{ height: '52px', width: 'auto' }}
          />
          <img
            src="/quotation-assets/digitaliza-sercotec.png"
            alt="Digitaliza tu Almacen - SERCOTEC"
            style={{ height: '52px', width: 'auto' }}
          />
          <img
            src="/quotation-assets/logo-blueexpress.png"
            alt="Blue Express - Copec"
            style={{ height: '36px', width: 'auto' }}
          />
        </div>

        {renderFooter()}
      </div>

      {/* ============================================ */}
      {/* PAGE 4: PAYMENT METHODS                      */}
      {/* ============================================ */}
      <div style={{ ...pageStyle, pageBreakBefore: 'always' }}>
        {renderHeader()}

        {/* Payment methods */}
        <div style={sectionTitleStyle}>Formas de pago para Precios Normal:</div>
        <ol
          style={{
            paddingLeft: '24px',
            fontSize: '12px',
            lineHeight: '2.2',
            color: '#444',
            marginBottom: '24px',
          }}
        >
          <li>Precio Normal con tarjeta de credito bancaria sin interes.</li>
          <li>Precio Normal con tarjeta de credito en 2, 3, 6, 10 y 12 cuotas.</li>
          <li>Precio Normal con tarjeta de debito (redcompra).</li>
          <li>Precio Contado con transferencia electronica, con descuento incluido.</li>
          <li>Precio Normal con cheque al dia.</li>
          <li>Precios aplican para una sola unidad de cada producto del kit.</li>
        </ol>

        {/* Bank details */}
        <div
          style={{
            fontSize: '13px',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '10px',
          }}
        >
          Datos para Pagos mediante transferencia electronica o deposito:
        </div>
        <div
          style={{
            background: '#F5F5F5',
            border: '1px solid #E0E0E0',
            borderRadius: '6px',
            padding: '14px 18px',
            fontSize: '12px',
            lineHeight: '1.9',
            color: '#333',
            marginBottom: '28px',
          }}
        >
          <div>
            <strong>Banco:</strong> Banco de Chile - Cuenta Corriente
          </div>
          <div>
            <strong>Razon Social:</strong> Club Almacen E.I.R.L.
          </div>
          <div>
            <strong>RUT:</strong> 76.166.790-6
          </div>
          <div>
            <strong>Nro. cuenta:</strong> 007-04649-03
          </div>
          <div>
            <strong>Email:</strong> clubalmacen@clubalmacen.com
          </div>
        </div>

        {/* Signature */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '4px',
            marginTop: '20px',
          }}
        >
          <img
            src="/quotation-assets/firma-carlos.png"
            alt="Firma Carlos Raggio"
            style={{
              height: '70px',
              width: 'auto',
            }}
          />
        </div>

        {/* Legal representative */}
        <div
          style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#333',
            marginBottom: '24px',
          }}
        >
          <div style={{ fontWeight: 'bold' }}>Carlos Raggio Figueroa</div>
          <div style={{ color: '#666', marginTop: '2px' }}>
            Representante Legal - Club Almacen E.I.R.L.
          </div>
        </div>

        {/* Campaign banner at bottom */}
        {campaign.name && (
          <div
            style={{
              textAlign: 'center',
              marginBottom: '20px',
            }}
          >
            <img
              src="/quotation-assets/banner-cybermonday.png"
              alt={campaign.name}
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                borderRadius: '6px',
              }}
            />
          </div>
        )}

        {renderFooter()}
      </div>
    </div>
  );
});

QuotationTemplate.displayName = 'QuotationTemplate';

export default QuotationTemplate;
